import React, { useState, useRef, useCallback } from 'react';
import { Button } from 'react-bootstrap';

/**
 * SpeechInput - Real-time speech-to-text using Deepgram
 *
 * Props:
 *   onTranscript: function(text, isFinal) - Called with transcribed text
 *     - text: The transcribed text (cumulative for the session)
 *     - isFinal: true when a sentence/phrase is complete, false for interim
 *   onStart: function() - Called when recording starts
 *   onEnd: function() - Called when recording ends
 *   onError: function(error) - Called on error
 *   language: string - Language code (default: 'en-US')
 *   className: string - Additional CSS classes
 *   variant: string - Bootstrap button variant (default: 'outline-secondary')
 *   size: string - Bootstrap button size ('sm', 'lg', or default)
 *   disabled: boolean - Disable the button
 *   showLabel: boolean - Show "Speak" label next to icon (default: false)
 */
const SpeechInput = ({
  onTranscript,
  onStart,
  onEnd,
  onError,
  language = 'en-US',
  className = '',
  style = {},
  variant = 'outline-secondary',
  size,
  disabled = false,
  showLabel = false
}) => {
  const [isListening, setIsListening] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const fullTranscriptRef = useRef('');

  const stopListening = useCallback(() => {
    // Stop media recorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    // Close WebSocket
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.close();
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setIsListening(false);
    onEnd?.();
  }, [onEnd]);

  const startListening = useCallback(async () => {
    setIsConnecting(true);
    fullTranscriptRef.current = '';

    try {
      // 1. Get API key from backend
      const keyResponse = await fetch('/api/speech/deepgram-key');
      const keyData = await keyResponse.json();

      if (!keyData.success) {
        throw new Error(keyData.error || 'Failed to get Deepgram API key');
      }

      const apiKey = keyData.key;

      // 2. Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      streamRef.current = stream;

      // 3. Connect to Deepgram WebSocket
      const socket = new WebSocket(
        `wss://api.deepgram.com/v1/listen?` +
        `encoding=linear16&sample_rate=16000&channels=1&` +
        `model=nova-2&language=${language}&` +
        `punctuate=true&interim_results=true&utterance_end_ms=1000`,
        ['token', apiKey]
      );

      socketRef.current = socket;

      socket.onopen = () => {
        setIsConnecting(false);
        setIsListening(true);
        onStart?.();

        // 4. Start streaming audio using AudioContext
        // Don't specify sample rate - let it match the input device
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);

        // Get the actual sample rate for resampling
        const inputSampleRate = audioContext.sampleRate;
        const targetSampleRate = 16000;

        processor.onaudioprocess = (e) => {
          if (socket.readyState === WebSocket.OPEN) {
            const inputData = e.inputBuffer.getChannelData(0);

            // Resample if needed
            let outputData;
            if (inputSampleRate !== targetSampleRate) {
              const ratio = inputSampleRate / targetSampleRate;
              const newLength = Math.round(inputData.length / ratio);
              outputData = new Float32Array(newLength);
              for (let i = 0; i < newLength; i++) {
                const srcIndex = Math.floor(i * ratio);
                outputData[i] = inputData[srcIndex];
              }
            } else {
              outputData = inputData;
            }

            // Convert float32 to int16
            const int16Data = new Int16Array(outputData.length);
            for (let i = 0; i < outputData.length; i++) {
              int16Data[i] = Math.max(-32768, Math.min(32767, outputData[i] * 32768));
            }
            socket.send(int16Data.buffer);
          }
        };

        source.connect(processor);
        processor.connect(audioContext.destination);

        // Store for cleanup
        mediaRecorderRef.current = {
          stop: () => {
            processor.disconnect();
            source.disconnect();
            audioContext.close();
          }
        };
      };

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'Results' && data.channel?.alternatives?.[0]) {
          const transcript = data.channel.alternatives[0].transcript;
          const isFinal = data.is_final;

          if (transcript) {
            if (isFinal) {
              // Add to full transcript
              fullTranscriptRef.current = fullTranscriptRef.current
                ? fullTranscriptRef.current + ' ' + transcript
                : transcript;
              onTranscript?.(fullTranscriptRef.current, true);
            } else {
              // Send interim with full transcript + current interim
              const interimText = fullTranscriptRef.current
                ? fullTranscriptRef.current + ' ' + transcript
                : transcript;
              onTranscript?.(interimText, false);
            }
          }
        }
      };

      socket.onerror = (error) => {
        console.error('Deepgram WebSocket error:', error);
        onError?.('Connection error. Please try again.');
        stopListening();
      };

      socket.onclose = (event) => {
        // Check for credit/billing related closures
        const reason = event.reason?.toLowerCase() || '';
        const isCreditsIssue =
          event.code === 4002 || // Deepgram insufficient credits code
          event.code === 402 ||  // Payment required
          reason.includes('insufficient') ||
          reason.includes('credit') ||
          reason.includes('balance') ||
          reason.includes('quota') ||
          reason.includes('limit exceeded');

        if (isCreditsIssue) {
          console.error('Deepgram credits exhausted:', event.code, event.reason);
          onError?.('Deepgram credits exhausted. Please add credits at console.deepgram.com');
        } else if (event.code === 4001 || reason.includes('auth') || reason.includes('unauthorized')) {
          console.error('Deepgram auth error:', event.code, event.reason);
          onError?.('Deepgram API key invalid. Please check your API key.');
        } else if (event.code !== 1000) {
          console.warn('Deepgram WebSocket closed unexpectedly:', event.code, event.reason);
        }

        setIsListening(false);
        setIsConnecting(false);
      };

    } catch (error) {
      console.error('Speech input error:', error);
      setIsConnecting(false);
      setIsListening(false);

      if (error.name === 'NotAllowedError') {
        onError?.('Microphone access denied. Please allow microphone access.');
      } else {
        onError?.(error.message || 'Failed to start speech recognition.');
      }
    }
  }, [language, onStart, onEnd, onError, onTranscript, stopListening]);

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return (
    <Button
      variant={isListening ? 'danger' : variant}
      size={size}
      onClick={toggleListening}
      disabled={disabled || isConnecting}
      className={className}
      title={isListening ? 'Click to stop' : 'Click to speak'}
      style={isListening ? { ...style, animation: 'pulse 1.5s infinite' } : style}
    >
      {isConnecting ? (
        <>
          <SpinnerIcon />
          {showLabel && <span className="ms-1">Connecting...</span>}
        </>
      ) : isListening ? (
        <>
          <MicOnIcon />
          {showLabel && <span className="ms-1">Listening...</span>}
        </>
      ) : (
        <>
          <MicIcon />
          {showLabel && <span className="ms-1">Speak</span>}
        </>
      )}

      {/* Pulse animation for listening state */}
      <style>{`
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); }
          100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
        }
      `}</style>
    </Button>
  );
};

// Microphone icons (inline SVG to avoid dependency)
const MicIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z"/>
    <path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0v5zM8 0a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V3a3 3 0 0 0-3-3z"/>
  </svg>
);

const MicOnIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M5 3a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0V3z"/>
    <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z"/>
  </svg>
);

const SpinnerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="spinner-icon">
    <path d="M8 0a8 8 0 1 0 8 8h-2a6 6 0 1 1-6-6V0z"/>
    <style>{`
      .spinner-icon {
        animation: spin 1s linear infinite;
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
  </svg>
);

export default SpeechInput;
